import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { Alert } from '../../components/ui/Alert';
import { ExplicitToggle } from '../../components/ui/ExplicitToggle';
import { Button } from '../../components/ui/Button';
import { EmojiButton } from '../../components/ui/EmojiPicker';
import { Field } from '../../components/ui/Field';
import { Select, Textarea } from '../../components/ui/form';
import { Modal } from '../albums/Modal';
import { ApiError, api } from '../../lib/api';
import { useEmojiComposer } from '../../lib/emoji';

interface Genre {
  id: number;
  name: string;
}

export interface EditableTrack {
  id: number;
  title: string;
  description: string | null;
  visibility: 'public' | 'unlisted' | 'private';
  explicit: boolean;
  genre_id?: number | null;
}

/**
 * Edit a track's title, description, genre and visibility.
 *
 * Only the things a person wrote. Not the measurements the pipeline took
 * (duration, loudness) and not the counters other people moved (plays,
 * favourites) - the server refuses those regardless, but showing them in an
 * edit form would suggest otherwise.
 *
 * The same shape as `AlbumEditor` on purpose. The two are not one component
 * because the fields genuinely differ - a track has a genre, an album has a
 * running order - and merging them would mean a form that renders half its
 * inputs conditionally.
 */
export function TrackEditor({
  track,
  onClose,
  onSaved,
}: {
  track: EditableTrack;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(track.title);
  const [description, setDescription] = useState(track.description ?? '');
  const [visibility, setVisibility] = useState(track.visibility);
  const [explicit, setExplicit] = useState(track.explicit);
  const [genreId, setGenreId] = useState(track.genre_id ? String(track.genre_id) : '');

  const emoji = useEmojiComposer(description, setDescription);

  const { data: genres } = useQuery({
    queryKey: ['genres'],
    queryFn: () => api.get<{ items: Genre[] }>('/genres'),
    // Effectively static: it changes when somebody edits a migration.
    staleTime: 60 * 60 * 1000,
  });

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/tracks/${track.id}`, {
        title: title.trim(),
        description: description.trim() || null,
        visibility,
        explicit,
        // An empty select means "no genre", which is a real value rather than
        // "leave it alone" - so it is sent as null instead of being omitted.
        genre_id: genreId ? Number(genreId) : null,
      }),
    onSuccess: onSaved,
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (!title.trim()) return;

    save.mutate();
  };

  return (
    <Modal title="Edit track" onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-5" data-track-form>
        <Field
          label="Title"
          name="title"
          value={title}
          maxLength={120}
          required
          autoFocus
          disabled={save.isPending}
          onChange={(event) => setTitle(event.target.value)}
          hint="Changing this changes the track's web address too."
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="track-edit-description" className="text-sm font-medium text-soft">
            Description
          </label>
          <Textarea
            id="track-edit-description"
            ref={emoji.ref as React.RefObject<HTMLTextAreaElement>}
            className="min-h-24"
            value={description}
            maxLength={2000}
            disabled={save.isPending}
            placeholder="Credits, gear, what it's about — optional."
            onChange={(event) => setDescription(event.target.value)}
          />
          <div className="-mt-0.5 flex">
            <EmojiButton onSelect={emoji.insert} direction="down" align="left" />
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="track-edit-genre" className="text-sm font-medium text-soft">
              Genre
            </label>
            <Select
              id="track-edit-genre"
              value={genreId}
              disabled={save.isPending}
              data-track-genre
              placeholder="Not sure yet"
              options={(genres?.items ?? []).map((genre) => ({
                value: String(genre.id),
                label: genre.name,
              }))}
              onChange={setGenreId}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="track-edit-visibility" className="text-sm font-medium text-soft">
              Who can hear it
            </label>
            <Select
              id="track-edit-visibility"
              value={visibility}
              disabled={save.isPending}
              data-track-visibility
              options={[
                { value: 'public', label: 'Everyone' },
                { value: 'unlisted', label: 'Anyone with the link' },
                { value: 'private', label: 'Only me' },
              ]}
              onChange={(next) => setVisibility(next as EditableTrack['visibility'])}
            />
          </div>
        </div>

        {/* Full width under the pair, for the reason the upload form gives. */}
        <ExplicitToggle value={explicit} onChange={setExplicit} disabled={save.isPending} />

        {/*
          Said here because it surprises people: making a track private does not
          take it off an album, it just stops appearing on one to everybody but
          the owner.
        */}
        {visibility !== 'public' && (
          <p className="text-xs text-faint">
            {visibility === 'private'
              ? 'Nobody but you can play it. It stays on any album it is part of, hidden from everyone else.'
              : 'Hidden from your profile. Anyone you send the link to can play it.'}
          </p>
        )}

        {save.isError && (
          <Alert tone="error">
            {save.error instanceof ApiError ? save.error.message : 'That could not be saved.'}
          </Alert>
        )}

        <div className="flex items-center gap-3">
          <Button type="submit" loading={save.isPending} disabled={!title.trim()}>
            Save changes
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
